import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Track, CreateFeedbackRequest, CreateTimestampedNoteRequest, Artist } from '../types/artist';
import { trackApi } from '../services/trackApi';
import { feedbackApi } from '../services/feedbackApi';
import { genreApi } from '../services/genreApi';
import { artistApi } from '../services/artistApi';
import { API_BASE_URL } from '../config/api';
import analytics from '../services/analytics';
import { usePageTracking } from '../hooks/usePageTracking';
import { ArtistOverview } from './shared/ArtistOverview';
import { TrackCard } from './shared/TrackCard';
import { TransportControl } from './shared/TransportControl';
import { FeedbackForm } from './shared/FeedbackForm';
import { ReportModal } from './shared/ReportModal';
import { 
  FeedbackPageLayout, 
  FeedbackMainLayout, 
  FeedbackSection, 
  FeedbackMessage, 
  FeedbackEmptyState 
} from './shared/FeedbackPageLayout';
import './GiveFeedbackTab.css';

// Mobile breakpoint - must match the CSS media query in GiveFeedbackTab.css and FeedbackPageLayout.css
const MOBILE_BREAKPOINT = 768;

const GiveFeedbackTab: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Track page view
  usePageTracking('Give Feedback');
  const [topTracks, setTopTracks] = useState<Track[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);
  
  // Genre filtering state
  const [availableGenres, setAvailableGenres] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [genreLoading, setGenreLoading] = useState(false);
  const [showGenreFilter, setShowGenreFilter] = useState(false);
  const [hasInitializedGenres, setHasInitializedGenres] = useState(false);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [fullArtist, setFullArtist] = useState<Artist | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  
  // Feedback form state
  const [overallRating, setOverallRating] = useState(5);
  const [compositionRating, setCompositionRating] = useState(5);
  const [musicQualityRating, setMusicQualityRating] = useState(5);
  const [productionQualityRating, setProductionQualityRating] = useState(5);
  const [review, setReview] = useState('');
  const [timestampedNotes, setTimestampedNotes] = useState<CreateTimestampedNoteRequest[]>([]);
  const [currentNote, setCurrentNote] = useState('');
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerSectionRef = useRef<HTMLDivElement>(null);

  const loadAvailableGenres = async () => {
    try {
      setGenreLoading(true);
      const genres = await genreApi.getAllGenres();
      setAvailableGenres(genres);
    } catch (err) {
      console.error('Failed to load genres:', err);
      // Don't set error state for genres since it's not critical
    } finally {
      setGenreLoading(false);
    }
  };

  const loadTopTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const tracks = await trackApi.getTopTracks(10, selectedGenres.length > 0 ? selectedGenres : undefined);
      setTopTracks(tracks);
    } catch (err) {
      console.error('Failed to load top tracks:', err);
      setError(err instanceof Error ? err.message : 'Failed to load top tracks');
    } finally {
      setLoading(false);
    }
  }, [selectedGenres]);

  useEffect(() => {
    if (isAuthenticated) {
      loadAvailableGenres();
      loadTopTracks();
    }
  }, [isAuthenticated, loadTopTracks]);

  // Auto-select all genres when they're first loaded (only once on initial load)
  useEffect(() => {
    if (availableGenres.length > 0 && !hasInitializedGenres) {
      setSelectedGenres([...availableGenres]);
      setHasInitializedGenres(true);
    }
  }, [availableGenres, hasInitializedGenres]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => {
      // Only update duration if metadata has already been loaded
      if (audio.readyState >= 1) { // HAVE_METADATA or higher
        const audioDuration = audio.duration;
        if (isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0) {
          setDuration(audioDuration);
        } else {
          setDuration(0);
        }
      }
    };
    
    const handleLoadedMetadata = () => {
      // Reset current time when new metadata loads
      setCurrentTime(0);
      audio.currentTime = 0;
      
      // Set duration from metadata
      const audioDuration = audio.duration;
      if (isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
      } else {
        setDuration(0);
      }
      
      // Clear any previous errors since metadata loaded successfully
      setError(null);
      
      console.log('Audio metadata loaded:', {
        duration: audioDuration,
        readyState: audio.readyState,
        networkState: audio.networkState
      });
    };
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e: Event) => {
      console.error('Audio playback error:', e);
      setError('Failed to load audio file');
      setIsPlaying(false);
      setDuration(0);
      setCurrentTime(0);
    };
    const handleCanPlay = () => setError(null);
    const handlePlay = () => {
      setIsPlaying(true);
      if (selectedTrack?.id) {
        analytics.trackPlay(selectedTrack.id.toString(), 'give_feedback');
      }
    };
    const handlePause = () => {
      setIsPlaying(false);
      if (selectedTrack?.id) {
        // Get current time directly from audio element to avoid dependency issues
        const currentAudioTime = audio.currentTime || 0;
        analytics.trackPause(selectedTrack.id.toString(), currentAudioTime);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [selectedTrack]);

  // Update audio volume when volume or mute state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handleTrackSelect = (track: Track) => {
    if (selectedTrack?.id === track.id) {
      // Deselect if same track
      setSelectedTrack(null);
      setIsPlaying(false);
      setShowFeedbackForm(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      // Reset metadata when deselecting
      setCurrentTime(0);
      setDuration(0);
      return;
    }
    
    setSelectedTrack(track);
    setIsPlaying(false);
    
    // Reset metadata immediately when selecting new track
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setShowFeedbackForm(true);  // Automatically show feedback form
    setFeedbackSuccess(null);
    resetFeedbackForm();
    
    // Scroll to player on mobile devices after track selection
    setTimeout(() => {
      if (playerSectionRef.current && window.innerWidth <= MOBILE_BREAKPOINT) {
        playerSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
    
    // Set the audio source
    setTimeout(() => {
      if (track.id && audioRef.current) {
        // Primary approach: use API endpoint for audio streaming
        const streamUrl = trackApi.getAudioStreamUrl(track.id);
        console.log('Using stream URL:', streamUrl);
        audioRef.current.src = streamUrl;
        audioRef.current.load();
      } else if (track.audioUrl && audioRef.current) {
        // Fallback: try direct URL approach
        const fullAudioUrl = track.audioUrl.startsWith('http') 
          ? track.audioUrl 
          : `${API_BASE_URL}${track.audioUrl}`;
        console.log('Fallback: setting audio source to:', fullAudioUrl);
        audioRef.current.src = fullAudioUrl;
        audioRef.current.load();
      }
    }, 150);
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !selectedTrack) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(err => {
        console.error('Failed to play audio:', err);
        setError('Failed to play audio file. The file may still be processing or there was a playback error.');
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number): string => {
    // Handle invalid or non-finite values
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }
  };

  const handleMuteToggle = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    analytics.muteToggle(newMutedState);
  };

  const handleGenreToggle = (genre: string) => {
    const newSelectedGenres = selectedGenres.includes(genre) 
      ? selectedGenres.filter(g => g !== genre)
      : [...selectedGenres, genre];
    
    setSelectedGenres(newSelectedGenres);
    analytics.genreFilter(newSelectedGenres, availableGenres.length);
  };

  const clearAllGenres = () => {
    setSelectedGenres([]);
    analytics.genreClearAll();
  };

  const selectAllGenres = () => {
    setSelectedGenres([...availableGenres]);
    analytics.genreSelectAll();
  };

  const addTimestampedNote = () => {
    if (!currentNote.trim()) return;
    
    const newNote: CreateTimestampedNoteRequest = {
      timestampInSeconds: currentTime,
      note: currentNote.trim()
    };
    
    setTimestampedNotes(prev => [...prev, newNote]);
    setCurrentNote('');
  };

  const removeTimestampedNote = (index: number) => {
    setTimestampedNotes(prev => prev.filter((_, i) => i !== index));
  };

  const resetFeedbackForm = () => {
    setOverallRating(5);
    setCompositionRating(5);
    setMusicQualityRating(5);
    setProductionQualityRating(5);
    setReview('');
    setTimestampedNotes([]);
    setCurrentNote('');
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTrack?.id || !user?.id) return;

    setSubmittingFeedback(true);
    setError(null);

    try {
      const feedbackData: CreateFeedbackRequest = {
        trackId: selectedTrack.id,
        overallRating,
        compositionRating,
        musicQualityRating,
        productionQualityRating,
        review: review.trim() || undefined,
        timestampedNotes: timestampedNotes.length > 0 ? timestampedNotes : undefined
      };

      await feedbackApi.createFeedback(feedbackData);
      
      // Track feedback submission
      analytics.feedbackSubmit(overallRating, timestampedNotes.length > 0);
      
      setFeedbackSuccess('Feedback submitted successfully! Thank you for helping the community.');
      setShowFeedbackForm(false);
      resetFeedbackForm();
      
      // Refresh top tracks to get updated feedback counts
      loadTopTracks();
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleArtistModalOpen = async (artistId?: number) => {
    if (!artistId) return;
    
    setShowArtistModal(true);
    
    try {
      const artist = await artistApi.getArtistById(artistId);
      setFullArtist(artist);
    } catch (error) {
      console.error('Failed to fetch artist details:', error);
      // Keep the modal open but show basic info from track
      setFullArtist(selectedTrack?.artist || null);
    }
  };

  const closeArtistModal = () => {
    setShowArtistModal(false);
    setFullArtist(null);
  };

  const handleReportTrack = async (reason: string): Promise<void> => {
    if (!selectedTrack?.publicId) {
      throw new Error('No track selected to report');
    }
    await trackApi.reportTrack(selectedTrack.publicId, { reason });
  };

  if (!isAuthenticated) {
    return (
      <div className="give-feedback-tab">
        <div className="auth-required">
          <h2>Give Feedback</h2>
          <p>Please log in to listen to tracks and provide feedback to the community.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="give-feedback-tab">
        <div className="loading">Loading top tracks...</div>
      </div>
    );
  }

  if (error && !selectedTrack) {
    return (
      <div className="give-feedback-tab">
        <div className="error">
          <p>Error: {error}</p>
          <button className="retry-button" onClick={loadTopTracks}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <FeedbackPageLayout>
      {/* Genre Filter */}
      <FeedbackSection className="genre-filter-section">
        <div className="filter-header">
          <button 
            className="filter-toggle-btn"
            onClick={() => setShowGenreFilter(!showGenreFilter)}
          >
            🎵 Filter by Genre {showGenreFilter ? '▼' : '▶'}
          </button>
          {selectedGenres.length > 0 && (
            <span className="active-filters">
              ({selectedGenres.length} genre{selectedGenres.length !== 1 ? 's' : ''} selected)
            </span>
          )}
        </div>
        
        {showGenreFilter && (
          <div className="genre-filter-content">
            {genreLoading ? (
              <div className="genre-loading">Loading genres...</div>
            ) : availableGenres.length > 0 ? (
              <>
                <div className="filter-controls">
                  <button 
                    className="filter-control-btn"
                    onClick={selectAllGenres}
                    disabled={selectedGenres.length === availableGenres.length}
                  >
                    Select All
                  </button>
                  <button 
                    className="filter-control-btn"
                    onClick={clearAllGenres}
                    disabled={selectedGenres.length === 0}
                  >
                    Clear All
                  </button>
                </div>
                <div className="genre-checkboxes">
                  {availableGenres.map(genre => (
                    <label key={genre} className="genre-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedGenres.includes(genre)}
                        onChange={() => handleGenreToggle(genre)}
                      />
                      <span className="checkbox-label">{genre}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <div className="no-genres">No genres available</div>
            )}
          </div>
        )}
      </FeedbackSection>

      {/* Main Content Layout */}
      <FeedbackMainLayout
        rightColumnRef={playerSectionRef}
        leftColumn={
          <div className="top-tracks-section">
            <h3>Top Tracks Awaiting Feedback</h3>
            {selectedTrack && (
              <div className="mobile-scroll-hint">
                ↑ Scroll up to play and review "{selectedTrack.title}"
              </div>
            )}
            {topTracks.length === 0 ? (
              <FeedbackEmptyState
                title="No tracks available for feedback at the moment."
                description="Check back later for new uploads!"
              />
            ) : (
              <div className="tracks-list">
                {topTracks.map(track => (
                  <TrackCard
                    key={track.id}
                    track={track}
                    isSelected={selectedTrack?.id === track.id}
                    isPlaying={isPlaying}
                    onClick={handleTrackSelect}
                    onPlayPause={handlePlayPause}
                    showArtist={true}
                    showFeedbackCount={true}
                    showFeedbackSummary={false}
                    showShareButton={false}
                    showDeleteButton={false}
                    showReviewStatus={false}
                  />
                ))}
              </div>
            )}
          </div>
        }
        rightColumn={
          selectedTrack ? (
            <>
              {/* Audio Player */}
              {(selectedTrack.audioUrl || selectedTrack.id) && (
                <TransportControl
                  track={selectedTrack}
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  volume={volume}
                  isMuted={isMuted}
                  onPlayPause={handlePlayPause}
                  onSeek={handleSeek}
                  onVolumeChange={handleVolumeChange}
                  onMuteToggle={handleMuteToggle}
                  showCloseButton={true}
                  onClose={() => setSelectedTrack(null)}
                  onArtistClick={handleArtistModalOpen}
                  audioRef={audioRef}
                />
              )}

              {/* Producer Notes */}
              {selectedTrack.producerNotes && selectedTrack.producerNotes.length > 0 && (
                <FeedbackSection className="producer-notes-section">
                  <h3>Producer Notes</h3>
                  <p>{selectedTrack.producerNotes}</p>
                </FeedbackSection>
              )}

              {/* Success Message */}
              {feedbackSuccess && (
                <FeedbackMessage type="success">
                  {feedbackSuccess}
                </FeedbackMessage>
              )}

              {/* Error Message */}
              {error && (
                <FeedbackMessage type="error">
                  {error}
                </FeedbackMessage>
              )}

              {/* Feedback Form */}
              {showFeedbackForm && (
                <FeedbackForm
                  trackTitle={selectedTrack.title}
                  overallRating={overallRating}
                  setOverallRating={setOverallRating}
                  compositionRating={compositionRating}
                  setCompositionRating={setCompositionRating}
                  musicQualityRating={musicQualityRating}
                  setMusicQualityRating={setMusicQualityRating}
                  productionQualityRating={productionQualityRating}
                  setProductionQualityRating={setProductionQualityRating}
                  review={review}
                  setReview={setReview}
                  timestampedNotes={timestampedNotes}
                  currentNote={currentNote}
                  setCurrentNote={setCurrentNote}
                  addTimestampedNote={addTimestampedNote}
                  removeTimestampedNote={removeTimestampedNote}
                  currentTime={currentTime}
                  formatTime={formatTime}
                  onSubmit={handleFeedbackSubmit}
                  onCancel={() => setShowFeedbackForm(false)}
                  submittingFeedback={submittingFeedback}
                  onReport={() => setShowReportModal(true)}
                />
              )}
            </>
          ) : (
            <FeedbackEmptyState
              title="Select a track to play and provide feedback"
              description="Choose a track from the list on the left to start listening and writing your review."
            />
          )
        }
      />

      {/* Artist Overview Modal */}
      {showArtistModal && (fullArtist || selectedTrack?.artist) && (
        <ArtistOverview 
          artist={fullArtist || selectedTrack!.artist!}
          showAsModal={true}
          onClose={closeArtistModal}
        />
      )}

      {/* Report Track Modal */}
      {showReportModal && selectedTrack && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onSubmit={handleReportTrack}
          title="Report Content Violation"
          itemName={selectedTrack.title}
          itemType="track"
        />
      )}
    </FeedbackPageLayout>
  );
};

export default GiveFeedbackTab;